import { motion } from "framer-motion";
import { Award, Users, Globe, Heart } from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

const values = [
  {
    icon: Heart,
    title: "Tutku",
    description: "Kahveye olan tutkumuz, her çekirdekte, her fincanda hissedilir.",
  },
  {
    icon: Award,
    title: "Kalite",
    description: "Sadece en iyi çekirdekler, en özenli işçilikle buluşur.",
  },
  {
    icon: Users,
    title: "Gelenek",
    description: "Yüzyıllık Türk kahvesi geleneğini geleceğe taşıyoruz.",
  },
  {
    icon: Globe,
    title: "Sürdürülebilirlik",
    description: "Doğaya saygılı, sürdürülebilir üretim prensiplerimiz.",
  },
];

const timeline = [
  { year: "1554", event: "İlk kahvehane İstanbul'da açıldı" },
  { year: "1600", event: "Türk kahvesi Avrupa'ya yayıldı" },
  { year: "2013", event: "UNESCO Somut Olmayan Kültürel Miras Listesi'ne alındı" },
  { year: "2024", event: "SümbülAğa bu mirası modern yorumla sunuyor" },
];

export default function AboutPage() {
  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section
        className="relative py-32"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1772744182940-56b3ce1b8bae?w=1920&q=80')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
        data-testid="about-hero"
      >
        <div className="absolute inset-0 bg-[#0D3B2E]/70" />
        <div className="section-container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="caption-text text-[#C5A059] mb-4 block">
              Hakkımızda
            </span>
            <h1 className="heading-xl text-[#F9F7F2] mb-6">
              Beş Yüzyıllık
              <br />
              <span className="text-gradient-gold">Miras</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-spacing bg-[#F9F7F2]" data-testid="about-content">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeUp}>
              <h2 className="heading-lg text-[#0D3B2E] mb-6">
                SümbülAğa Türk Kahvesi
              </h2>
              <div className="decorative-line mb-8" />
              <div className="space-y-6 body-text text-[#5D4037]">
                <p>
                  SümbülAğa Türk Kahvesi, kahveyi yalnızca bir içecek değil; kültür,
                  sohbet ve zarafet unsuru olarak görür. Geleneksel Türk kahvesi
                  mirasını çağdaş bir premium marka diliyle yeniden sunar.
                </p>
                <p>
                  Osmanlı saraylarından günümüze uzanan bu eşsiz lezzet, SümbülAğa
                  ile modern dünyaya uyum sağlarken özünü hiç kaybetmeden yaşamaya
                  devam ediyor.
                </p>
                <p>
                  Her çekirdek, dünyanın en seçkin kahve bölgelerinden özenle
                  seçilir. Geleneksel kavurma teknikleriyle hazırlanan kahvemiz,
                  modern kalite standartlarıyla birleşerek benzersiz bir deneyim
                  sunar.
                </p>
                <p>
                  SümbülAğa, sadece bir kahve markası değil; Türk kahvesi kültürünü
                  dünyaya taşıyan bir elçidir. Her fincan, beş yüzyıllık bir
                  geleneğin, misafirperverliğin ve zarafetin temsilcisidir.
                </p>
              </div>
            </motion.div>
            <motion.div {...fadeUp} className="relative">
              <img
                src="https://images.unsplash.com/photo-1544787219-7f47ccb76574?w=800&q=80"
                alt="Türk kahvesi hazırlığı"
                className="w-full aspect-[4/5] object-cover"
              />
              <div className="absolute -bottom-8 -right-8 bg-[#C5A059] text-[#0D3B2E] p-8 max-w-xs hidden md:block">
                <span className="text-4xl font-display">UNESCO</span>
                <p className="caption-text mt-2">
                  Somut Olmayan Kültürel Miras
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-spacing bg-white" data-testid="about-values">
        <div className="section-container">
          <motion.div {...fadeUp} className="text-center mb-16">
            <span className="caption-text text-[#C5A059] mb-4 block">
              Değerlerimiz
            </span>
            <h2 className="heading-lg text-[#0D3B2E]">Bizi Biz Yapan</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                {...fadeUp}
                transition={{ delay: index * 0.1 }}
                className="text-center p-8 border border-[#E5E0D8] hover:border-[#C5A059] transition-colors"
              >
                <value.icon
                  className="w-10 h-10 text-[#C5A059] mx-auto mb-4"
                  strokeWidth={1.5}
                />
                <h3 className="heading-md text-[#0D3B2E] mb-3">{value.title}</h3>
                <p className="body-text text-[#5D4037] text-sm">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section-spacing bg-[#0D3B2E]" data-testid="about-timeline">
        <div className="section-container">
          <motion.div {...fadeUp} className="text-center mb-16">
            <span className="caption-text text-[#C5A059] mb-4 block">
              Tarihçe
            </span>
            <h2 className="heading-lg text-[#F9F7F2]">
              Kahvenin Yolculuğu
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto">
            {timeline.map((item, index) => (
              <motion.div
                key={index}
                {...fadeUp}
                transition={{ delay: index * 0.15 }}
                className="flex gap-8 mb-12 last:mb-0"
              >
                <div className="w-24 flex-shrink-0 text-right">
                  <span className="font-display text-3xl text-[#C5A059]">
                    {item.year}
                  </span>
                </div>
                <div className="relative pl-8 border-l border-[#F9F7F2]/20">
                  <div className="absolute left-0 top-2 w-3 h-3 bg-[#C5A059] -translate-x-1/2" />
                  <p className="body-text text-[#F9F7F2]/80">{item.event}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="section-spacing bg-[#F9F7F2]" data-testid="about-quote">
        <div className="section-container">
          <motion.div {...fadeUp} className="max-w-4xl mx-auto text-center">
            <div className="text-6xl text-[#C5A059] font-display mb-6">"</div>
            <blockquote className="font-display text-3xl md:text-4xl text-[#0D3B2E] leading-tight mb-8">
              Bir fincan kahvenin kırk yıl hatırı vardır.
            </blockquote>
            <p className="caption-text text-[#5D4037]">— Türk Atasözü</p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
